<script setup lang="ts">
import { useVersionCheck } from '@/composables/useVersionCheck'

/**
 * Das Banner erscheint bewusst nur, wenn der automatische Reload nicht laufen
 * kann: während einer Bearbeitung (sonst gingen Eingaben verloren) oder wenn
 * das Versuchslimit gegriffen hat. Ansonsten lädt die Seite unmittelbar neu,
 * ganz ohne Hinweis.
 */
const { updateAvailable, newVersion, autoReloadBlocked, isEditing, reload } = useVersionCheck()
</script>

<template>
  <Teleport to="body">
    <Transition name="slide-down">
      <div
        v-if="updateAvailable && (isEditing || autoReloadBlocked)"
        class="update-banner-shell"
      >
        <div class="update-banner" data-testid="update-banner">
          <svg class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
          </svg>
          <span v-if="isEditing" class="update-banner__text" data-testid="update-banner-editing">
            Neue Version <strong>{{ newVersion }}</strong> verfügbar – wird nach
            dem Speichern automatisch geladen.
          </span>
          <span v-else class="update-banner__text" data-testid="update-banner-blocked">
            Neue Version <strong>{{ newVersion }}</strong> verfügbar – der
            automatische Reload hat nicht funktioniert.
          </span>
          <button
            class="update-banner__button"
            data-testid="update-banner-reload"
            type="button"
            @click="reload"
          >
            Jetzt aktualisieren
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.35s ease, opacity 0.35s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.update-banner-shell {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 10050;
  padding-top: calc(env(safe-area-inset-top, 0px) + 0.5rem);
  padding-right: max(0.75rem, env(safe-area-inset-right, 0px));
  padding-left: max(0.75rem, env(safe-area-inset-left, 0px));
  pointer-events: none;
}

.update-banner {
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 0 auto;
  width: min(100%, 52rem);
  border: 1px solid var(--sh-banner-border);
  border-radius: 1rem;
  background: var(--sh-banner-bg);
  color: var(--sh-banner-text);
  box-shadow: 0 16px 36px var(--sh-banner-shadow);
  backdrop-filter: blur(10px);
  padding: 0.85rem 1rem;
}

.update-banner__text {
  min-width: 0;
  flex: 1;
  font-size: 0.95rem;
  line-height: 1.35;
}

.update-banner__button {
  flex-shrink: 0;
  border: 0;
  border-radius: 999px;
  background: var(--sh-banner-button);
  color: var(--sh-banner-text);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 700;
  min-height: 2.75rem;
  padding: 0.7rem 1rem;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.update-banner__button:hover {
  background: var(--sh-banner-button-hover);
}

@media (max-width: 640px) {
  .update-banner {
    align-items: stretch;
    flex-direction: column;
    gap: 0.65rem;
    text-align: center;
  }

  .update-banner__button {
    width: 100%;
  }
}
</style>
